import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ButtonLink, ClosingCTA, Container, Eyebrow, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { StepFlow } from '../components/StepFlow';
import { FaqSection } from '../components/FaqSection';
import { Seo } from '../components/seo/Seo';
import { images, pillars, serviceCategories, steps } from '../data/site';
import { faqs, pageSeo } from '../data/seo';
import { breadcrumbSchema, faqSchema, legalServiceSchema, localBusinessSchema, organizationSchema, webPageSchema, websiteSchema } from '../utils/schema';
const headingClass = sectionHeadingClass;
const seo = pageSeo.home;
export function Home() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.hero} schemas={[organizationSchema, websiteSchema, legalServiceSchema, localBusinessSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema([{
      label: 'Home',
      path: '/'
    }]), faqSchema(faqs)]} data-id="element-203" data-mp="3kvk5ok4kl15z" />

      {/* Hero */}
      <section aria-labelledby="hero-heading" className="border-b border-line" data-id="element-204" data-mp="2qwsgkjjt672o">
        <Container className="pb-14 pt-14 md:pb-20 md:pt-24" data-id="element-205" data-mp="2mw8jffz8j3gj">
          <Eyebrow data-id="element-206" data-mp="2hxnl5m3jmtuq">Ontario &amp; Quebec</Eyebrow>
          <h1 id="hero-heading" className="mt-7 max-w-[16ch] font-display text-[42px] font-medium leading-[0.99] tracking-tightest text-ink sm:text-[56px] md:mt-8 md:text-[82px] lg:text-[100px]" data-id="element-207" data-mp="2hzi4ivz62dwp">
            The AI-native law firm.
          </h1>
          <div className="mt-9 grid gap-8 md:mt-10 md:grid-cols-12 md:items-end" data-id="element-208" data-mp="23a2kdyu556pz">
            <p className="max-w-prose text-[16.5px] leading-relaxed text-graphite md:col-span-6 md:text-[19px]" data-id="element-209" data-mp="1z7pbj1k3ksoz">
              NexLegal combines technology, AI, and legal expertise to create a more
              modern legal experience of lawyers for individuals and businesses.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap md:col-span-6 md:justify-end" data-id="element-210" data-mp="1omu2yga8o55k">
              <ButtonLink to="/how-it-works" data-id="element-211" data-mp="1anck0hjmjm44">Learn how NexLegal works</ButtonLink>
              <ButtonLink to="/contact" variant="outline" data-id="element-212" data-mp="1fle495ssdrh6">
                Contact us
              </ButtonLink>
            </div>
          </div>
        </Container>

        <Container className="pb-16 md:pb-24" data-id="element-213" data-mp="23dbbejlajvby">
          <figure className="overflow-hidden rounded-sm bg-sand" data-id="element-214" data-mp="28aky3rhapsw3">
            <img src={images.hero} alt="Lawyer standing by the window of a light-filled modern law office" width={1600} height={900} loading="eager" decoding="async" fetchPriority="high" className="aspect-[3/2] w-full object-cover sm:aspect-[16/9]" data-id="element-215" data-mp="1tlf20cdr8isf" />
          </figure>
        </Container>
      </section>

      {/* What is NexLegal */}
      <Section ariaLabel="A smarter way to access legal help" data-id="element-216" data-mp="fk97bdifyljy">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-217" data-mp="an9214tf1mtw">
          <div className="md:col-span-6" data-id="element-218" data-mp="akwl8uze9iif">
            <Eyebrow data-id="element-219" data-mp="oedtvkqjlato">What is NexLegal</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-220" data-mp="og8e0wtzoywp">
              A smarter way to access legal help.
            </h2>
            <p className="mt-6 max-w-prose text-[16.5px] leading-relaxed text-graphite md:mt-7 md:text-[17px]" data-id="element-221" data-mp="3ss2hx465vvpo">
              NexLegal is a modern law firm built around technology, structured
              workflows, and lawyer oversight. We use AI to help organize and prepare
              legal work, while lawyers provide review, judgment, and professional
              guidance.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-222" data-mp="1h2jg6bx8v7vm">
              About NexLegal
              <ArrowForwardIcon sx={{
              fontSize: 16
            }} data-id="element-223" data-mp="27fpr22qdnv5c" />
            </Link>
          </div>
          <figure className="md:col-span-6" data-id="element-224" data-mp="2cdhu9swh33ou">
            <img src={images.team} alt="Two legal professionals reviewing documents together using modern legal technology" width={1200} height={900} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover" data-id="element-225" data-mp="2hccdgvycqu9h" />
          </figure>
        </div>
      </Section>

      {/* Why NexLegal */}
      <section aria-labelledby="why-heading" className="border-y border-line bg-sand/60" data-id="element-226" data-mp="302ajz7mi4fb0">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-227" data-mp="3526r3fqhwmus">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-228" data-mp="353q2ot17i9q8">
            <h2 id="why-heading" className={`md:col-span-7 ${headingClass}`} data-id="element-229" data-mp="3a2b7l5relhl0">
              Why NexLegal
            </h2>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-230" data-mp="3e08svxi1zjj4">
              Four principles shape how the firm is designed and how the work gets
              done.
            </p>
          </div>
          <ul className="mt-12 grid gap-px overflow-hidden rounded-sm bg-line md:mt-14 md:grid-cols-2" data-id="element-231" data-mp="tds5g6cewocx">
            {pillars.map(pillar => <li key={pillar.title} className="bg-paper p-7 sm:p-8 md:p-10" data-id="element-232" data-mp="oh1lh7xhl8m8">
                <h3 className="font-display text-[21px] font-medium tracking-tight text-ink md:text-[22px]" data-id="element-233" data-mp="1m1ny8zyv1w4f">
                  {pillar.title}
                </h3>
                <p className="mt-3 max-w-prose text-[15.5px] leading-relaxed text-graphite" data-id="element-234" data-mp="1ccw6osxppdu1">
                  {pillar.body}
                </p>
              </li>)}
          </ul>
        </Container>
      </section>

      {/* How it works */}
      <Section ariaLabel="How NexLegal works" data-id="element-235" data-mp="24ursonjwkd03">
        <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-236" data-mp="2028gnv0v5pxe">
          <div className="md:col-span-7" data-id="element-237" data-mp="200xf81uw4u9g">
            <Eyebrow data-id="element-238" data-mp="3ubncggokaoqr">How it works</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-239" data-mp="3ucoz7iexwzob">How NexLegal works.</h2>
          </div>
          <div className="md:col-span-5 md:pb-2 md:text-right" data-id="element-240" data-mp="28w5zn6hi6z7">
            <ButtonLink to="/how-it-works" variant="outline" data-id="element-241" data-mp="3kjzquweos1x7">
              See the full model
            </ButtonLink>
          </div>
        </div>
        <StepFlow steps={steps} data-id="element-242" data-mp="3amlbl04i1j6h" />
      </Section>

      {/* Services snapshot */}
      <section aria-labelledby="services-heading" className="border-y border-line" data-id="element-243" data-mp="3aopqse7um4m2">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-244" data-mp="3fmzl6mlr116m">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-245" data-mp="31mgilv01ynbu">
            <div className="md:col-span-7" data-id="element-246" data-mp="36l04xwik614q">
              <Eyebrow data-id="element-247" data-mp="36lt0s2fzb683">Services</Eyebrow>
              <h2 id="services-heading" className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-248" data-mp="36muob6e6ll6p">
                Legal services for modern needs.
              </h2>
            </div>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-249" data-mp="14t4vlnjry8po">
              Four categories covering the legal needs individuals, families, and
              businesses meet most often.
            </p>
          </div>

          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 md:mt-14 lg:grid-cols-4" data-id="element-250" data-mp="1nqf5xll9uvrx">
            {serviceCategories.map(category => <div key={category.id} className="border-t border-ink pt-5 md:pt-6" data-id="element-251" data-mp="1ixvqsk6xrsl0">
                <h3 className="font-display text-[20px] font-medium leading-snug tracking-tight text-ink md:text-[21px]" data-id="element-252" data-mp="1xmi3ee56i68j">
                  {category.title}
                </h3>
                <ul className="mt-4 space-y-2 md:mt-5" data-id="element-253" data-mp="26nq1ow7p372m">
                  {category.items.slice(0, 4).map(item => <li key={item} className="text-[14.5px] text-mist" data-id="element-254" data-mp="22nnzmly1sbmt">
                      {item}
                    </li>)}
                </ul>
              </div>)}
          </div>

          <div className="mt-12 md:mt-14" data-id="element-255" data-mp="8ay6w7jf18h0">
            <ButtonLink to="/services" variant="outline" data-id="element-256" data-mp="wula2egx8ere">
              All services
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Responsible AI */}
      <Section ariaLabel="Technology with lawyer oversight" data-id="element-257" data-mp="16k4tpmcgkcnb">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-258" data-mp="16ko8iu6takx4">
          <figure className="order-2 md:order-1 md:col-span-5" data-id="element-259" data-mp="1akevsnx4qiww">
            <img src={images.abstract} alt="Abstract network of connected lines representing AI-supported legal workflows" width={1200} height={900} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover md:aspect-[4/5]" data-id="element-260" data-mp="dv7ddort4px7" />
          </figure>
          <div className="order-1 md:order-2 md:col-span-7" data-id="element-261" data-mp="rsm4qz1mf7rt">
            <Eyebrow data-id="element-262" data-mp="1ya99f7p9wbi7">Responsible AI</Eyebrow>
            <h2 className={`mt-6 max-w-[20ch] md:mt-7 ${headingClass}`} data-id="element-263" data-mp="23a5e795so6yt">
              Technology with lawyer oversight.
            </h2>
            <p className="mt-6 max-w-prose font-serif text-[21px] font-light leading-[1.36] text-ink md:text-[24px]" data-id="element-264" data-mp="2dd0ws55krr12">
              Technology supports the work. Lawyers stand behind it.
            </p>
            <ul className="mt-9 divide-y divide-line border-y border-line md:mt-10" data-id="element-265" data-mp="23n7n59oztlzg">
              {['AI supports the process — organizing, preparing, and structuring.', 'Lawyers review the work and remain accountable for the advice.', 'Client trust and confidentiality guide every tool we adopt.', 'Innovation is paired with professional responsibility, always.'].map(point => <li key={point} className="py-5 text-[15.5px] leading-relaxed text-graphite md:text-[16px]" data-id="element-266" data-mp="1go30ujm39n2b">
                  {point}
                </li>)}
            </ul>
            <Link to="/how-it-works" className="mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-267" data-mp="3b35m2543uizf">
              How responsible AI works at NexLegal
              <ArrowForwardIcon sx={{
              fontSize: 16
            }} data-id="element-268" data-mp="mlpyewuupv3h" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Ontario & Quebec */}
      <section aria-labelledby="jurisdiction-heading" className="border-y border-line bg-sand/60" data-id="element-269" data-mp="hlv7itb2t5if">
        <Container className="py-16 md:py-20" data-id="element-270" data-mp="3l4odajxm4q21">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-10" data-id="element-271" data-mp="3l5zkyuu6ihyf">
            <div className="md:col-span-7" data-id="element-272" data-mp="3oyeqmbeddyw">
              <Eyebrow data-id="element-273" data-mp="3pq2xc2o09f5">Jurisdictions</Eyebrow>
              <h2 id="jurisdiction-heading" className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-274" data-mp="3v1jdr8dg50iy">
                Serving Ontario and Quebec.
              </h2>
            </div>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-275" data-mp="32dx2ddxz8rlj">
              NexLegal is initially focused on Ontario and Quebec, with additional
              jurisdictions considered over time.{' '}
              <Link to="/services" className="text-ink underline decoration-line underline-offset-4 transition-colors hover:decoration-navy" data-id="element-276" data-mp="1mncwo06pu5fh">
                See what we cover
              </Link>
              .
            </p>
          </div>
        </Container>
      </section>

      {/* About */}
      <section aria-labelledby="about-heading" className="border-b border-line" data-id="element-277" data-mp="20kj819p411uo">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-278" data-mp="25jcptebfjf0n">
          <h2 id="about-heading" className="sr-only" data-id="element-279" data-mp="1rkmh11f8zqwg">
            About NexLegal
          </h2>
          <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-16" data-id="element-280" data-mp="1wjzesh7nqidt">
            <figure className="md:col-span-5" data-id="element-281" data-mp="g5zidg80tavb">
              <img src={images.portrait} alt="Portrait of a legal professional working in a quiet, daylit office" width={1000} height={1250} loading="lazy" decoding="async" className="aspect-[4/5] w-full rounded-sm object-cover" data-id="element-282" data-mp="g57l80yrz52x" />
            </figure>
            <div className="md:col-span-7" data-id="element-283" data-mp="2d3wueizbfd57">
              <Eyebrow data-id="element-284" data-mp="2d2lpgfu4vzci">About NexLegal</Eyebrow>
              <p className="mt-6 font-serif text-[23px] font-light leading-[1.34] text-ink sm:text-[27px] md:mt-7 md:text-[34px]" data-id="element-285" data-mp="292bjv4a97ay6">
                NexLegal was created to explore a better legal model — one that combines
                the strength of legal professionals with the speed and structure of
                modern technology.
              </p>
              <div className="mt-8 md:mt-9" data-id="element-286" data-mp="1q4ifdxjzesyo">
                <ButtonLink to="/about" variant="outline" data-id="element-287" data-mp="1q5k3b2m3j9xt">
                  Read our story
                </ButtonLink>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <FaqSection data-id="element-288" data-mp="3kjuqz7qyfy20" />

      <ClosingCTA data-id="element-289" data-mp="3kjb73lhquzlc" />
    </>;
}